import CommonMsg from "@com/loading/CommonMsg";

const OPTIONS = {
  fftSize: 1024,
  bufferLen: 1024,
  smoothingTimeConstant: 0.2,
  minCaptureFreq: 85, // in Hz
  maxCaptureFreq: 255, // in Hz
  noiseCaptureDuration: 1000, // in ms
  minNoiseLevel: 0.3, // from 0 to 1
  maxNoiseLevel: 0.7, // from 0 to 1
  avgNoiseMultiplier: 1.2,
};

const SN_LEN = 16;
const TIME_LEN = 8;

let voice = 0;
let timeConnect = 0;
let timeOutStamp;

class AudioPlayer {

  /** ws地址 */
  url;
  ws = null;

  /** ws连接成功 */
  onopen;

  /** ws传输数据 */
  onmessage;

  /** ws连接失败 */
  onerror;

  /** ws关闭 */
  onclose;

  /** ws断开或者出错，是否自动重连，默认为true */
  autoReconnect = true;

  /** 音频编码格式，默认aac */
  audioCodecType = 'aac';
  audioTrack;
  caseId;
  devices;
  playerList = [];
  playing = false;
  encoder;

  constructor(option) {
    Object.keys(option).forEach(key => this[key] = option[key]);
    // 编码器初始化一次即可
    initEncoder({
      audioCodecType: this.audioCodecType,
      encodeSuccess: chunk => {
        const result = audioEncoded(chunk, this.caseId);
        if (voice > 0.1) {
          this.ws?.send(result);
        }
      },
    })
      .then(encoder => this.encoder = encoder);
  }

  /** 初始化 */
  init() {
    this.ws = new WebSocket(this.url);
    this.ws.onopen = evt => {
      console.log('websocket open');
      setTimeout(() => {
        CommonMsg.success('服务器连接成功');
        this.onopen && this.onopen(evt);
      }, 1000);
      this.playing = true;
      timeConnect = 0;
      timeOutStamp && clearTimeout(timeOutStamp);
      navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        .then(async (stream) => {
          const [audio] = stream.getAudioTracks();
          this.audioTrack = audio;
          accAudioEncode({
            audioTrack: this.audioTrack,
            audioEncoder: this.encoder,
          });
          calcVoice(stream);
        })
        .finally(() => {
          this.devices.forEach(({ device }) => {
            this.createPlayer(`audio_${device?.deviceId}`);
          });
        });
    };
    this.ws.onmessage = async evt => {
      const buffer = await evt.data.arrayBuffer();
      const decoder = new TextDecoder('utf-8');
      // modal
      const modal = decoder.decode(buffer.slice(0, 1));
      // caseId
      const caseId = decoder.decode(buffer.slice(1, 1 + this.caseId.length)).slice(4, 23);
      // sn
      const sn = decoder.decode(buffer.slice(this.caseId.length + 1 + 2, this.caseId.length + 1 + 2 + SN_LEN)).slice(0, SN_LEN - 1);
      const totalLen = buffer.byteLength;
      // data
      const data = new Uint8Array(buffer.slice(this.caseId.length + 1 + 2 + SN_LEN + TIME_LEN, totalLen));
      this.playerList.forEach(player => {
        if (player?.node?.id?.includes(sn)) {
          player.feed({ audio: data });
        }
      });
      this.onmessage && this.onmessage(evt, { modal, caseId, sn, data });
    };
    this.ws.onerror = err => {
      console.log('websocket error', err);
      this.onerror && this.onerror(err);
      // ws重连
      if (this.autoReconnect) {

        if (timeConnect >= 10) {
          CommonMsg.error('服务器连接失败，请刷新页面重试或者联系管理员');
          timeOutStamp && clearTimeout(timeOutStamp);
          timeConnect = 0;
        }

        timeConnect++;
        CommonMsg.error(`服务器连接错误，第${timeConnect}次重连中......`);
        timeOutStamp && clearTimeout(timeOutStamp);
        timeOutStamp = setTimeout(() => this.init(), 5000);
      }
    };
    this.ws.onclose = evt => {
      console.log('websocket close');
      this.playing = false;
      this.onclose && this.onclose(evt);
    };
  }

  /** 播放aac音频 */
  play(caseId, devices = []) {
    this.caseId = 'CASE' + caseId + '000000000';
    this.devices = devices;
    this.init();
  }

  /** 静音，同时停止aac音频传输 */
  mute(id, type = 'auto') {
    const audio = document.querySelector(`#audio_${id}`);
    if (type === 'auto') {
      if (audio.classList.length === 0) {
        audio.muted = true;
        audio.classList.add('audio_muted');
      } else {
        audio.muted = false;
        audio.classList.remove('audio_muted');
      }
    } else if (!type) {
      audio.muted = false;
      audio.classList.remove('audio_muted');
    } else {
      audio.muted = true;
      audio.classList.add('audio_muted');
    }
  }

  /** 退出对讲，断开ws连接 */
  stop() {
    this.audioTrack && this.audioTrack.stop();
    this.ws && (this.ws.onerror = null);
    this.ws && this.ws.close();
    this.ws = null;
    timeConnect = 0;
  }

  /** 创建播放器 */
  createPlayer(node) {
    const jmuxer = new window.JMuxer({
      node,
      mode: 'audio',
      flushingTime: 0.1,
      clearBuffer: true,
      maxDelay: 0,
      debug: false,
      // fps: 43,
    });
    this.playerList.push(jmuxer);
    return this.playerList;
  }
}

/** 初始化音频编码器 */
const initEncoder = async ({ audioCodecType, encodeSuccess, encodeError }) => {
  const AEncoder = audioCodecType === "aac" ? window.AACEncoder : window.AudioEncoder;
  const audioEncoder = new AEncoder({
    output: chunk => {
      encodeSuccess && encodeSuccess(chunk);
    },
    error: err => {
      console.log("AACEncoder error", err);
      encodeError && encodeError(err);
    },
  });
  await audioEncoder.configure({
    encoderPath: './js/encoderWorker.min.js',
    codec: audioCodecType, // aac
    biterate: 64000,
    numberOfChannels: 2,
    sampleRate: 48000,
    profile: 'AAC-LC'
  });

  return Promise.resolve(audioEncoder);
};

/** aac音频编码 */
const accAudioEncode = async ({
  audioTrack,
  audioEncoder,
}) => {
  const processor = new window.MediaStreamTrackProcessor(audioTrack);
  const reader = processor.readable.getReader();

  while (true) {
    const result = await reader.read();

    if (result.done) {
      // audioEncoder.close();
      break;
    }
    audioEncoder.encode(result.value);
    result.value.close();
  }
};

/** aac编码成功 */
const audioEncoded = (chunk, caseId, model = 'C') => {
  if (chunk.byteLength === 0) {
    return;
  }
  const chunkData = new Uint8Array(chunk.byteLength);
  chunk.copyTo(chunkData);
  const timeStamp = new Date().getTime();
  const size = chunkData.byteLength + caseId.length + model.length + 2 + TIME_LEN; // 2是size自身的长度，8是timeStamp的长度
  const result = addHeader(chunkData, model, caseId, timeStamp, size);
  return result;
};

/** 添加aac音频头 */
function addHeader(data, model, id, timeStamp, size) {
  // 处理总长度
  const HEXsize = size.toString(16).padStart(4, '0');
  const sizeBytes = HEXsize.match(/.{1,2}/g).map(byte => parseInt(byte, 16));
  const sizeUint8Array = Uint8Array.from(sizeBytes);

  // 处理时间戳
  const timeBuffer = new ArrayBuffer(TIME_LEN); // 创建一个8字节的ArrayBuffer
  const timeView = new DataView(timeBuffer); // 创建一个DataView以便进行字节操作
  timeView.setUint32(0, Math.floor(timeStamp / 4294967296), false); // 高位字节
  timeView.setUint32(4, timeStamp >>> 0, false); // 低位字节
  const timeUint8 = new Uint8Array(timeBuffer);

  // 构造信息头
  const header = new Uint8Array(3 + id.length + TIME_LEN);
  header.set([model.charCodeAt(), id.length], 0);
  header.set(new TextEncoder().encode(id), 1);
  header.set(sizeUint8Array, 1 + id.length);
  header.set(timeUint8, 1 + id.length + 2);

  // 将信息头添加到音频数据的头部
  const result = new Uint8Array(data.length + header.length);
  result.set(header, 0);
  result.set(data, header.length);

  return result;
}

/** 计算aac音频音量 */
const calcVoice = stream => {
  let baseLevel = 0;
  let voiceScale = 1;
  let envFreqRange = [];
  let isNoiseCapturing = true;
  let captureTimeout = null;

  window.AudioContext = window.AudioContext || window.webkitAudioContext;
  const audioContext = new AudioContext();

  const source = audioContext.createMediaStreamSource(stream);
  const analyser = audioContext.createAnalyser();
  analyser.smoothingTimeConstant = OPTIONS.smoothingTimeConstant;
  analyser.fftSize = OPTIONS.fftSize;
  const scriptProcessorNode = audioContext.createScriptProcessor(OPTIONS.bufferLen, 1, 1);

  source.connect(analyser);
  analyser.connect(scriptProcessorNode);
  scriptProcessorNode.connect(audioContext.destination);

  scriptProcessorNode.onaudioprocess = () => {
    const frequencies = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(frequencies);
    const average = window.analyserFrequency(analyser, frequencies, OPTIONS.minCaptureFreq, OPTIONS.maxCaptureFreq);

    if (isNoiseCapturing) {
      captureTimeout = setTimeout(() => {
        isNoiseCapturing = false;
        envFreqRange = envFreqRange.filter(val => val).sort();
        var averageEnvFreq = envFreqRange.length ? envFreqRange.reduce((p, c) => Math.min(p, c), 1) : (OPTIONS.minNoiseLevel || 0.1);

        baseLevel = averageEnvFreq * OPTIONS.avgNoiseMultiplier;
        if (OPTIONS.minNoiseLevel && baseLevel < OPTIONS.minNoiseLevel) baseLevel = OPTIONS.minNoiseLevel;
        if (OPTIONS.maxNoiseLevel && baseLevel > OPTIONS.maxNoiseLevel) baseLevel = OPTIONS.maxNoiseLevel;

        voiceScale = 1 - baseLevel;

        scriptProcessorNode.onaudioprocess = () => {
          const frequencies = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(frequencies);

          const average = window.analyserFrequency(analyser, frequencies, OPTIONS.minCaptureFreq, OPTIONS.maxCaptureFreq);
          if (isNoiseCapturing) {
            envFreqRange.push(average);
            return;
          }
          voice = Math.max(0, average - baseLevel) / voiceScale;
        };
      }, OPTIONS.noiseCaptureDuration);
    }
  };
};

window.analyserFrequency = analyserFrequencyAverage.bind(null, 255);
window.floatData = analyserFrequencyAverage.bind(null, 1);
window.frequencyToIndex = frequencyToIndex;

function analyserFrequencyAverage(div, analyser, frequencies, minHz, maxHz) {
  var sampleRate = analyser.context.sampleRate;
  var binCount = analyser.frequencyBinCount;
  var start = frequencyToIndex(minHz, sampleRate, binCount);
  var end = frequencyToIndex(maxHz, sampleRate, binCount);
  var count = end - start;
  var sum = 0;
  for (; start < end; start++) {
    sum += frequencies[start] / div;
  }
  return count === 0 ? 0 : (sum / count);
}

function frequencyToIndex(frequency, sampleRate, frequencyBinCount) {
  var nyquist = sampleRate / 2;
  var index = Math.round(frequency / nyquist * frequencyBinCount);
  return clamp(index, 0, frequencyBinCount);
}

function clamp(value, min, max) {
  return min < max
    ? (value < min ? min : value > max ? max : value)
    : (value < max ? max : value > min ? min : value);
}

export default AudioPlayer;

export const setSpeakLive = data => {
  sessionStorage.setItem('speakLive', JSON.stringify(data));
};

export const getSpeakLive = () => {
  return JSON.parse(sessionStorage.getItem('speakLive') || '{}');
};
